import { CalendarDays } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { DatePicker } from '@nucleus-commerce/admin-kit/date-time';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import {
  isMediaDatePreset,
  MEDIA_DATE_PRESETS,
  utcToday,
  type MediaDateFilterValue,
} from '../schemas/media-date-filter';

// A cleared or half-typed date reports '', which means no bound.
const dayOrUndefined = (value: string) => (value === '' ? undefined : value);

export interface MediaDateFilterProps {
  value: MediaDateFilterValue;
  onChange: (value: MediaDateFilterValue) => void;
  className?: string;
}

/** Upload date: a few presets, or a custom range typed as two days. */
export function MediaDateFilter({ value, onChange, className }: MediaDateFilterProps) {
  const { t } = useTranslation('media');
  const today = utcToday();
  const set = (patch: Partial<MediaDateFilterValue>) => onChange({ ...value, ...patch });

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      <Select
        value={value.preset}
        onValueChange={(next) => {
          if (!isMediaDatePreset(next)) return;
          // A custom range keeps what was typed, so switching away and back is not lossy.
          onChange(next === 'custom' ? { ...value, preset: next } : { preset: next });
        }}
      >
        <SelectTrigger className="w-[170px]" aria-label={t('date.label')}>
          <CalendarDays className="text-muted-foreground" aria-hidden />
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {MEDIA_DATE_PRESETS.map((preset) => (
            <SelectItem key={preset} value={preset}>
              {t(`date.${preset}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {value.preset === 'custom' ? (
        <div className="flex items-center gap-1.5">
          <DatePicker
            value={value.from ?? ''}
            max={value.to ?? today}
            timeZone="UTC"
            onChange={(day) => set({ from: dayOrUndefined(day) })}
            aria-label={t('date.from')}
            className="w-[150px]"
          />
          <span className="text-muted-foreground text-sm" aria-hidden>
            –
          </span>
          <DatePicker
            value={value.to ?? ''}
            min={value.from}
            max={today}
            timeZone="UTC"
            onChange={(day) => set({ to: dayOrUndefined(day) })}
            aria-label={t('date.to')}
            className="w-[150px]"
          />
        </div>
      ) : null}
    </div>
  );
}
